import React, { Fragment, Component } from 'react'
import PropTypes from 'prop-types';
import { Link } from 'react-router'
import { connect } from 'react-redux'
import moment from 'moment'
import { push } from 'react-router-redux'
import numeral from 'common/lib/numeral'
import { map, has, isNaN, keyBy } from 'lodash'
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  Tooltip as ChartTooltip
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import DocumentTitle from 'react-document-title'
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import {styles} from 'common/lib/styles';
import Select from "react-select";
import {ReactComponent as Co2Icon} from 'assets/images/co2.svg'
import {ReactComponent as InfoIcon} from 'assets/images/info-icon.svg'
import PartItem from 'client/components/PartItem';
import actions from 'client/actions'
import PartRow from 'client/components/PartRow';
import PartDialog from 'client/components/PartDialog';
import { isNumeral } from 'numeral';
import { Condition } from 'client/components/Condition';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  ChartTooltip
);

const OptionsRow = ({ estimates: { by_id, by_year, totals }, title, description, type, params, years, textWarning = true }) => {
    const optionCells = years.map(year => {
        if (by_year[year] && by_year[year][type]) {
            const currentYear = by_year[year]
            const currentEstimate = currentYear[type]
            return <td className={textWarning && "text-warning"} key={ year }>{ numeral(by_id[currentEstimate].estimate).format('0,0.0') }</td>
        }
        return <td key={ year }></td>
    })

    let total = (totals.by_id[type])
    if (isNaN(total)) {
        total = 0
    }

    return (
        <tr className={type === "free_form_total" ? "free-form-total" : ""}>
            <td key="Title">
                { title }<br />
                <span className="text-muted small">{ description }</span>
            </td>
            { optionCells }
            <td className={textWarning ? "text-warning" : ""} key="Samlet">{ numeral(total).format('0,0.0') }</td>
        </tr>
    )
}
OptionsRow.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    params: PropTypes.object.isRequired,
    years: PropTypes.array.isRequired,
    estimates: PropTypes.object.isRequired,
    textWarning: PropTypes.bool,
}

const YearOrdinarySubtotalsRow = ({ totals, years }) => {
    const totalCells = years.map(year => {
        if (totals.by_year[year]) {
            const currentSum = numeral(totals.by_year[year]).format('0,0.0')
            return <td key={ year }>{ currentSum }</td>
        }
        return <td key={ year }>0</td>
    })

    return (
        <tr>
            <td key="Title">Omkostninger til drift og vedligeholdelse <br/><span className="text-muted">Subtotal</span>
            </td>
            { totalCells }
            <td>{ numeral(totals.ordinary_total).format('0,0.00') }</td>
        </tr>
    )
}
YearOrdinarySubtotalsRow.propTypes = {
    totals: PropTypes.object.isRequired,
    years: PropTypes.array.isRequired,
    projectAmount: PropTypes.array.isRequired
}

const YearExtraordinarySubtotalsRow = ({ totals, years }) => {
    if (totals && totals.extraordinary_total == 0) {
        return null;
    }
    const totalCells = years.map(year => {
        return totals.extraordinary_by_year[year] ? <td key={ year }><span className="text-warning">{ numeral(totals.extraordinary_by_year[year]).format('0,0.0') }</span></td> : <td key={ year }><span className="text-warning">0</span></td>
    })

    return (
        <tr>
            <td key="Title">Ekstraordinær omkostning <br/><span className="text-muted">Subtotal</span>
            </td>
            { totalCells }
            <td className="text-warning">{ numeral(totals.extraordinary_total).format('0,0.0') }</td>
        </tr>
    )
}
YearExtraordinarySubtotalsRow.propTypes = {
    totals: PropTypes.object.isRequired,
    years: PropTypes.array.isRequired,
}

const YearTotalsRow = ({ totals, years }) => {
    const taxOptionCells = years.map(year => {
        if (totals.totals_by_year[year]) {
            return <td key={ year }>{ numeral(totals.totals_by_year[year] * 0.25).format('0,0.0') }</td>
        }
        return <td key={ year }>0</td>
    })
    const optionCells = years.map(year => {
        if (totals.totals_by_year[year]) {
            return <td key={ year }>{ numeral(totals.totals_by_year[year] * 1.25).format('0,0.0') }</td>
        }
        return <td key={ year }>0</td>
    })

    const total = totals.ordinary_total + totals.extraordinary_total
    const tax = total * 0.25;

    return (<>
            <tr>
                <td key="Title">Moms<br />
                </td>
                { taxOptionCells }
                <td>{ numeral(tax).format('0,0.0') }</td>
            </tr>
            <tr>
                <td key="Title">Total<br />
                </td>
                { optionCells }
                <td>{ numeral(total + tax).format('0,0.0') }</td>
            </tr>
        </>
    )
}
YearTotalsRow.propTypes = {
    totals: PropTypes.object.isRequired,
    years: PropTypes.array.isRequired,
}

const YearBarChart = ({ totals, years }) => {
    const data = map(years, year => {
        return has(totals.by_year, year)
            ? totals.by_year[year]
            : 0
    })
    const extraordinaryData = map(years, year => {
        return has(totals.extraordinary_by_year, year)
            ? totals.extraordinary_by_year[year]
            : 0
    })
    const options = {
        animation: false,
        layout: {
            padding: 10
        },
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            y: {
                stacked: true,
                ticks: {
                    font: {
                        family: 'Outfit',
                        weight: 300,
                    }
                },
                grid: {
                    color: '#F4EAE1',
                    lineWidth: '0.8'
                }
            },
            x: {
                stacked: true,
                ticks: {
                    font: {
                        family: 'Outfit',
                        weight: 300,
                    }
                },
                grid: {
                    display: false
                }
            }
        }
    }

    const chartData = {
        labels: years,
        datasets: [{
            label: 'Omkostninger til vedligeholdelse',
            data: data,
            borderRadius: 5,
            backgroundColor: '#6D8299',
        },
        {
            label: 'Ekstraordinær omkostning',
            data: extraordinaryData,
            borderRadius: 5,
            backgroundColor: '#D4A74C',
        }],
    }

    return (
        <Bar data={chartData} options={options} />
    )
}
YearBarChart.propTypes = {
    totals: PropTypes.object.isRequired,
    years: PropTypes.array.isRequired,
}

 
/**
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
const rowSort = (a, b) => {
    if (a.prioritized) {
        return -1;
    }
    if (a.condition > b.condition) {
        return 1;
    }
    return 0;
}

class Overview extends Component {

    static propTypes = {
        plan: PropTypes.object.isRequired,
        parts: PropTypes.array.isRequired,
        estimates: PropTypes.object.isRequired,
        params: PropTypes.object.isRequired,
        years: PropTypes.array.isRequired,
        route: PropTypes.object.isRequired,
        dispatch: PropTypes.func.isRequired,
    }

    constructor (props) {
        super(props)
        this.state = {
            currentPartId: null,
            isYearOpen: false,
            modalVisible: false,
        }
        this.onYearChange = this.onYearChange.bind(this)
        this.handleYearClick = this.handleYearClick.bind(this)
        this.toggleModal = this.toggleModal.bind(this)
        this.closeModal = this.closeModal.bind(this)
        this.formatEngergyEmission = this.formatEngergyEmission.bind(this)
        this.formatOrdinaryTotalsPerYear = this.formatOrdinaryTotalsPerYear.bind(this)
        this.formatEnergyExpensePerYear = this.formatEnergyExpensePerYear.bind(this)
        numeral.zeroFormat('')
    }

    componentDidMount() {
        const { dispatch, setBreadcrumb } = this.props
        dispatch(setBreadcrumb('Overblik'));
    }

    toggleModal (part_id) {
        this.setState({
            modalVisible: !this.state.modalVisible,
            currentPartId: part_id
        })
    }

    closeModal () {
        this.setState({
            modalVisible: !this.state.modalVisible,
        })
    }

    onYearChange (selected) {
        const { dispatch, params } = this.props

        dispatch(push(`/c/${params.customer_id}/activity-plan/${selected.value}`))
    }

    handleYearClick (e) {
        e.preventDefault();
        this.setState({isYearOpen: !this.state.isYearOpen})
    }

    hasEstimateType(estimateType) {
        const { estimates } = this.props;
        const estimatesById = estimates.by_id;

        let estimateMatch = null;

        for (let key in estimatesById) {
            if (estimatesById.hasOwnProperty(key) && estimateMatch === null) {
                const estimate = estimatesById[key];

                if (estimate[estimateType] && estimate.estimate > 0) {
                    estimateMatch = estimate;
                }
            }
        }

        return estimateMatch;
    }

    partLinkHandler (id) {
        const { dispatch, params } = this.props
        dispatch(push(`/c/${params.customer_id}/parts/${id}`))
    }

    formatEngergyEmission (plan) {
        if (!plan.total_area || !plan.energy_emission) {
            return null;
        }
        const formattedEnergyEmission = numeral(plan.energy_emission).format('0.00').replace(',','.');
        return numeral(formattedEnergyEmission * 1000 / plan.total_area / 15).format('0.00') + ' kg.';
    }

    formatOrdinaryTotalsPerYear(plan, estimates) {
        const area = plan.residential_area + plan.commercial_area;
        const total = estimates.totals.ordinary_total + estimates.totals.extraordinary_total;
        if (!total || !area) {
            return null;
        }
        return numeral((total) * 1250 / area / 15).format('0,0') + ' kr.';
    }

    formatEnergyExpensePerYear(plan) {
        if (!plan.energy_expense || !plan.total_area) {
            return null;
        }
        return numeral(plan.energy_expense / plan.total_area / 15).format('0,0') + ' kr.';
    }

    render () {
        const {
            plan, params: { customer_id },
            parts, sortedParts, estimates, years, route, sortedProjectParts,
            classes, projects, projectAmount
        } = this.props

        const partRows = map(sortedParts, (part, idx) => <PartRow linkHandler={() => this.toggleModal(part.id)} key={ idx } part={ part } years={ years } customer_id={ customer_id } { ...this.props } />)

        const freeFormEstimate = this.hasEstimateType("free_form");
        const freeFormTotalEstimate = this.hasEstimateType("free_form_total");

        const part = parts[this.state.currentPartId]
        let tableHeaders = years.concat() //eslint-disable-line prefer-const
        tableHeaders.unshift('Aktiviteter')
        tableHeaders.push('Samlet')
        const table = years.length > 0
            ? (
                <table className="table distribution-table-customer">
                    <thead>
                        <tr>{ tableHeaders.map((header, idx) => <th key={ idx }>{ header }</th>) }</tr>
                    </thead>
                    <tbody>
                        { partRows }
                        <OptionsRow title="Stillads/lift" description="" type="scaffolding" years={ years } { ...this.props } />
                        <OptionsRow title="Byggeplads" description="" type="construction_site" years={ years } { ...this.props } />
                        <OptionsRow title="Teknisk rådgiver" description="" type="adviser" years={ years } { ...this.props } />
                        <OptionsRow title="Byggesagsadministration" description="" type="building_management" years={ years } { ...this.props } />
                        <OptionsRow title="Uforudsete udgifter" description="" type="unforeseen_expenses" years={ years } { ...this.props } />
                        <OptionsRow title="Uspecificeret drift og vedligehold" description="" type="unspecified_maintenance" years={ years } { ...this.props } />
                        {freeFormEstimate !== null && (
                            <OptionsRow title={plan.estimate_free_form_title} description="" type={"free_form"} years={years} textWarning={false} { ...this.props }/>
                        )}
                        <YearOrdinarySubtotalsRow years={ years } totals= { estimates.totals } />
                        <YearExtraordinarySubtotalsRow years={ years } totals= { estimates.totals } />
                        <YearTotalsRow years={ years } totals= { estimates.totals } />
                        {freeFormTotalEstimate !== null && (
                            <OptionsRow title={plan.estimate_free_form_total_title} description="" type={"free_form_total"} years={years} textWarning={false} { ...this.props }/>
                        )}
                    </tbody>
                </table>
            )
            : null
        return (
            <div>
                <DocumentTitle title={ route.title } />

                <Grid container alignItems="stretch" spacing={3}>
                    <Grid item sm={12} md={6}>
                    <Paper className={classes.paper}>
                        <Typography className={classes.header} variant="h5">Konklusion</Typography>
                           {plan.condition && <Typography className={classes.marginBottom}>
                            <div className={classes.infoTableContainer}>
                                    <div className={classes.infoTable}>
                                        <Typography className={classes.greyText}>Ejendommens stand</Typography>
                                        <Typography className={classes.blueText}>
                                            <Condition condition={ plan.condition } hasText={ true } />
                                        </Typography>
                                    </div>
                                </div>
                            </Typography>}
                        <Typography className={classes.marginBottom}>
                            { plan.conclusion
                                ? plan.conclusion.split('\n').map((line, idx) => {
                                    return (
                                        <span key={ idx }>
                                        { line }
                                        <br />
                                        </span>
                                    )
                                }): null }
                        </Typography>
                        {plan.recommendations && <>
                            <Typography className={classes.header} variant="subtitle1">Anbefalinger</Typography>
                            <Typography paragraph>
                                { plan.recommendations }
                            </Typography>
                        </>}
                    </Paper>
                    </Grid>
                    <Grid className="page-break" item sm={12} md={6}>
                        {<Paper className={`${classes.paper}`}>
                            <Typography className={classes.header} variant="h5">Opgaver der bør prioriteres</Typography>
                            {projects.length ? <div className={classes.partGridContainer}>
                                { projects.map(p => (
                                    <PartItem
                                        linkHandler={() => this.partLinkHandler(p.part.id)}
                                        key={ p.id }
                                        part={ p.part }
                                        project={p}
                                        prioritized={p.prioritized}
                                        md={12}
                                    />)) }
                            </div> : <Typography component="div">Vedligeholdelsesplanen er udarbejdet før denne opdatering blev implementeret. Projekter der skal prioriteres, vil fremgå næste gang I vælger at få planen opdateret</Typography>}
                        </Paper>}
                    </Grid>
                    <Grid className={classes.grid} item sm={12} md={4}>
                        <Paper className={classes.paper}>
                            <Typography variant="h5">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.3092 10.0789C17.47 9.64777 17.9497 9.42866 18.3809 9.58927C19.4575 9.99065 20.4158 10.657 21.1667 11.5268C21.9175 12.3965 22.4365 13.4416 22.6764 14.5652C22.9164 15.6888 22.8691 16.8547 22.5391 17.9552C22.209 19.0557 21.6065 20.0553 20.7878 20.8614C19.969 21.6675 18.9603 22.2542 17.8548 22.5671C16.7493 22.88 15.5826 22.9092 14.4629 22.6518C13.3432 22.3943 12.3064 21.8587 11.4486 21.0945C10.5907 20.3302 9.93922 19.3615 9.55469 18.2787C9.40083 17.8451 9.62758 17.3689 10.0612 17.2149C10.4949 17.0609 10.971 17.2877 11.125 17.7214C11.4157 18.54 11.9087 19.2718 12.5573 19.8497C13.206 20.4276 13.9899 20.8328 14.8366 21.0274C15.6832 21.222 16.5652 21.2002 17.401 20.9636C18.2369 20.7271 18.9995 20.2833 19.6185 19.6739C20.2375 19.0645 20.6931 18.3087 20.9427 17.4766C21.1923 16.6446 21.2282 15.763 21.0469 14.9135C20.8655 14.0639 20.4726 13.2736 19.905 12.616C19.3373 11.9584 18.6128 11.4546 17.7988 11.1511C17.3676 10.9903 17.1485 10.5101 17.3092 10.0789ZM16.125 13.2865C16.4527 12.9635 16.9803 12.9673 17.3034 13.295L18.0033 14.0053C18.3247 14.3313 18.323 14.8553 17.9994 15.1791L15.179 17.9994C14.8536 18.3247 14.326 18.3248 14.0007 17.9994C13.6753 17.674 13.6754 17.1465 14.0007 16.821L16.2357 14.5854L16.1165 14.4649C15.7936 14.1372 15.7973 13.6096 16.125 13.2865ZM13.1667 8.00008C13.1667 5.14661 10.8535 2.83341 8 2.83341C5.14653 2.83341 2.83334 5.14661 2.83334 8.00008C2.83334 10.8536 5.14653 13.1667 8 13.1667C10.8535 13.1667 13.1667 10.8536 13.1667 8.00008ZM7.16667 10.0001V6.83341H7C6.53977 6.83341 6.16667 6.46032 6.16667 6.00008C6.16667 5.53984 6.53977 5.16675 7 5.16675H8C8.46024 5.16675 8.83334 5.53984 8.83334 6.00008V10.0001C8.83334 10.4603 8.46024 10.8334 8 10.8334C7.53977 10.8334 7.16667 10.4603 7.16667 10.0001ZM14.8333 8.00008C14.8333 11.774 11.774 14.8334 8 14.8334C4.22606 14.8334 1.16667 11.774 1.16667 8.00008C1.16667 4.22614 4.22606 1.16675 8 1.16675C11.774 1.16675 14.8333 4.22614 14.8333 8.00008Z" fill="#183649"/>
                                </svg>
                                Omkostninger
                            </Typography>
                            <div className={classes.infoTableContainer}>
                                <div className={`${classes.infoTable} ${classes.flexColumn}`}>
                                    <Typography variant="h6" className={classes.textCenter}>
                                        { numeral((estimates.totals.ordinary_total + estimates.totals.extraordinary_total) * 1250).format('0,0') } kr.
                                    </Typography>
                                    <Typography className={`${classes.textSmall} ${classes.textCenter}`}>
                                        Samlede omkostninger til vedligeholdelse over en 15 årig periode
                                    </Typography>
                                </div>
                                <div className={`${classes.infoTable} ${classes.flexColumn}`}>
                                    <Typography variant="h6" className={classes.textCenter}>
                                        { this.formatOrdinaryTotalsPerYear(plan, estimates)}
                                    </Typography>
                                    <Typography className={`${classes.textSmall} ${classes.textCenter}`}>
                                        Vedligehold pr. m<sup>2</sup> pr. år
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid>
                    {plan.energy_expense ? <Grid className={classes.grid} item sm={12} md={4}>
                        <Paper className={classes.paper}>
                            <Typography variant="h5">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13.367 1.17051C13.5915 1.14836 13.818 1.1835 14.0252 1.27272L14.1268 1.32155L14.2238 1.37884C14.4129 1.50157 14.5681 1.67021 14.6743 1.86908L14.7231 1.97129L14.7635 2.07676C14.8464 2.32624 14.8538 2.59573 14.7837 2.85085C14.7807 2.86165 14.7773 2.87273 14.7739 2.8834L12.854 8.90293C12.8499 8.91581 12.8451 8.92868 12.8403 8.94134C12.8309 8.96659 12.8279 8.99403 12.8312 9.02077C12.8346 9.04745 12.8445 9.0729 12.8599 9.09499C12.8752 9.11709 12.8959 9.13528 12.9198 9.14772C12.9437 9.16015 12.9703 9.16668 12.9972 9.1666H19.9998C20.3458 9.16593 20.6853 9.26324 20.9784 9.4472C21.272 9.63161 21.5077 9.89547 21.6574 10.2083C21.8071 10.5211 21.8647 10.8703 21.8241 11.2148C21.7833 11.5593 21.6455 11.8855 21.4269 12.1549C21.4114 12.174 21.3952 12.1926 21.3781 12.2102L11.4777 22.4101L11.477 22.4094C11.2824 22.6187 11.0265 22.7616 10.7446 22.8137C10.4473 22.8687 10.1402 22.8209 9.87353 22.6783C9.60686 22.5357 9.39652 22.3065 9.27718 22.0286C9.15785 21.7507 9.13644 21.4406 9.21663 21.149L9.22575 21.1165L11.1463 15.0969L11.1593 15.0585C11.1688 15.0333 11.1718 15.0058 11.1685 14.9791C11.1651 14.9524 11.1552 14.927 11.1398 14.9049C11.1244 14.8828 11.1038 14.8646 11.0799 14.8521C11.056 14.8398 11.0293 14.8332 11.0024 14.8333H3.99983V14.8326C3.65395 14.8332 3.31496 14.7366 3.02197 14.5527C2.72821 14.3683 2.49269 14.1044 2.34293 13.7916C2.19323 13.4788 2.13498 13.1295 2.17562 12.7851C2.21632 12.4406 2.35422 12.1144 2.57275 11.845L2.62223 11.7896L12.522 1.58978V1.59043C12.7167 1.38085 12.9736 1.23831 13.2557 1.18613L13.367 1.17051ZM3.85856 12.9081C3.84409 12.9299 3.8343 12.9543 3.83121 12.9804C3.82751 13.0117 3.83257 13.0438 3.84619 13.0722C3.85981 13.1005 3.8814 13.1245 3.90804 13.1412C3.93473 13.1579 3.96573 13.1667 3.99723 13.1666H10.9998C11.2955 13.1661 11.5871 13.2372 11.8494 13.3736C12.1124 13.5105 12.3382 13.7091 12.5076 13.9524C12.6771 14.1958 12.785 14.4765 12.8221 14.7708C12.8575 15.0515 12.8265 15.336 12.7336 15.6028L11.2511 20.2499L20.1392 11.093C20.1544 11.0708 20.1659 11.0463 20.1691 11.0195C20.1728 10.9882 20.1671 10.9561 20.1535 10.9277C20.1399 10.8994 20.1188 10.8754 20.0923 10.8587C20.0656 10.8419 20.0345 10.8332 20.0031 10.8333H12.9998V10.8326C12.7043 10.8331 12.4131 10.7626 12.1509 10.6262C11.8878 10.4894 11.6615 10.2908 11.492 10.0475C11.3226 9.80413 11.2146 9.52327 11.1776 9.2291C11.1422 8.94826 11.173 8.66335 11.2661 8.39642L12.7479 3.74928L3.85856 12.9081Z" fill="#183649"/>
                                </svg>
                                Energi
                            </Typography>
                            <div className={classes.infoTableContainer}>
                                <div className={`${classes.infoTable} ${classes.flexColumn}`}>
                                    <Typography variant="h6" className={classes.textCenter}>
                                        { numeral(plan.energy_expense).format('0,0') } kr.
                                    </Typography>
                                    <Typography className={`${classes.textSmall} ${classes.textCenter}`}>
                                        Samlet energiudgift pr. år
                                    </Typography>
                                </div>
                                <div className={`${classes.infoTable} ${classes.flexColumn}`}>
                                    <Typography variant="h6" className={classes.textCenter}>
                                        { this.formatEnergyExpensePerYear(plan) }
                                    </Typography>
                                    <Typography className={`${classes.textSmall} ${classes.textCenter}`}>
                                        Energiudgift pr. m<sup>2</sup> pr. år
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid> : null}
                    {plan.energy_emission ? <Grid className={classes.grid} item sm={12} md={4}>
                        <Paper className={classes.paper}>
                            <Typography variant="h5">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.1666 12.9997C11.1668 12.5396 11.5398 12.1664 12 12.1664C12.4601 12.1664 12.8332 12.5396 12.8333 12.9997V18.988L15.4108 16.4105C15.7362 16.0852 16.2638 16.0852 16.5892 16.4105C16.9146 16.7359 16.9145 17.2635 16.5892 17.5889L12.5892 21.5889C12.2637 21.9144 11.7362 21.9144 11.4108 21.5889L7.41079 17.5889C7.08547 17.2635 7.08539 16.7359 7.41079 16.4105C7.73619 16.0852 8.26376 16.0852 8.58917 16.4105L11.1666 18.988V12.9997ZM8.15753 2.21261C9.36469 2.08177 10.5861 2.2333 11.7246 2.65532C12.8632 3.07743 13.8885 3.75872 14.7187 4.6449C15.4051 5.37755 15.9422 6.23411 16.304 7.16639H17.5L17.7155 7.17095C18.7912 7.21431 19.8305 7.58259 20.6953 8.22954C21.6177 8.91968 22.2921 9.88996 22.6172 10.9952C22.9422 12.1004 22.9007 13.2814 22.4987 14.361C22.0966 15.4407 21.3555 16.3612 20.3867 16.9847C19.9997 17.2336 19.484 17.1217 19.235 16.7347C18.9861 16.3478 19.0981 15.8321 19.485 15.5831C20.151 15.1544 20.6604 14.5218 20.9368 13.7797C21.2132 13.0374 21.2417 12.2251 21.0182 11.4652C20.7947 10.7055 20.3313 10.0386 19.6972 9.56417C19.063 9.08968 18.292 8.83296 17.5 8.83306H15.7103C15.3418 8.83306 15.0167 8.59115 14.9114 8.238C14.6382 7.32194 14.1555 6.48185 13.5019 5.78423C12.8485 5.08678 12.0419 4.55075 11.1458 4.21847C10.2495 3.88618 9.28761 3.7665 8.33722 3.86951C7.38693 3.97254 6.4735 4.29501 5.66925 4.81157C4.86492 5.3282 4.19202 6.02526 3.7031 6.84673C3.21421 7.66818 2.92275 8.59197 2.85219 9.54529C2.78165 10.4986 2.93361 11.4551 3.2962 12.3396C3.65881 13.224 4.22198 14.0123 4.94138 14.6416C5.28776 14.9447 5.32311 15.471 5.02016 15.8174C4.71715 16.1638 4.19078 16.1992 3.84438 15.8962C2.93041 15.0967 2.21455 14.0953 1.75388 12.9717C1.29333 11.8483 1.10047 10.6331 1.19008 9.42225C1.27972 8.21124 1.64975 7.03735 2.27081 5.99386C2.89186 4.95047 3.74721 4.06544 4.76886 3.40923C5.79041 2.75315 6.95051 2.34349 8.15753 2.21261Z" fill="#183649"/>
                                </svg>
                                CO2
                            </Typography>
                            <div className={classes.infoTableContainer}>
                                <div className={`${classes.infoTable} ${classes.flexColumn}`}>
                                    <Typography variant="h6" className={classes.textCenter}>
                                        {numeral(plan.energy_emission).format('0.00')} ton
                                    </Typography>
                                    <Typography className={`${classes.textSmall} ${classes.textCenter}`}>
                                        Samlet CO2 udledning pr. år
                                    </Typography>
                                </div>
                                <div className={`${classes.infoTable} ${classes.flexColumn}`}>
                                    <Typography variant="h6" className={classes.textCenter}>
                                            {this.formatEngergyEmission(plan)}
                                    </Typography>
                                    <Typography className={`${classes.textSmall} ${classes.textCenter}`}>
                                        CO2 udledning pr. m<sup>2</sup> pr. år
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid> : null}
                    <Grid item md={12}>
                        <Paper className={`${classes.paper} ${classes.pageBreak}`}>
                            <div className={classes.chartHeader}>
                                <div className={classes.chartYear}>
                                    <div className={classes.chartYearText}><span className="label label-round label-blue">&nbsp;</span>Omkostninger til vedligeholdelse</div>
                                    {estimates.totals && Object.keys(estimates.totals.extraordinary_by_year).length ? <div className={classes.chartYearText}><span className="label label-round label-yellow">&nbsp;</span>Ekstraordinær omkostning</div> : null}
                                </div>
                                <div className={classes.chartYear}>
                                    <h3>15 år</h3>
                                </div>
                            </div>
                            <div className={classes.chart}>
                                <YearBarChart years={ years } totals= { estimates.totals } />
                            </div>
                        </Paper>
                    </Grid>
                    <Grid item md={12}>
                        <Paper className={`${classes.paper} ${classes.printAllowBreak}`}>
                            <Typography className={classes.header} variant="h5">
                                Fordelingsplan
                            </Typography>
                            <div className="table-responsive">
                                { table }
                            </div>
                        </Paper>
                    </Grid>
                </Grid>
                <PartDialog type="maintenance" part={part} closeModal={this.closeModal} customerId={customer_id} modalVisible={this.state.modalVisible} />
            </div>
        )
    }
}

const mapStateToProps = state => {
    const parts = state.data.customer.parts;
    return {
        plan: state.data.customer.plan,
        parts: keyBy(parts, 'id'),
        sortedParts: parts.sort((a, b) => {
                    return a.bips_id > b.bips_id ? 1 : -1
                }),
        projects: state.data.customer.projects.filter(p => p.prioritized),
        estimates: state.data.customer.estimates,
        years: state.data.years.years,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        setBreadcrumb: actions.setBreadcrumb,
        dispatch,
    }
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(Overview))
